/**
 * Pure helpers behind the "Export" button on the admin students screen.
 *
 * Both formats are produced in the browser from the same table shape
 * (`headers` + `rows` of plain strings):
 * - CSV: a UTF-8 file with BOM and `;` as separator, which Excel in Portuguese
 *   locales opens straight into columns (Google Sheets detects it too).
 * - PDF: a clean printable HTML page printed from a hidden iframe, which
 *   opens the browser print dialog where "Save as PDF" is available. No PDF
 *   library is needed for that.
 *
 * Nothing here touches React or i18n so it can be unit-tested directly.
 */

export type ExportTable = {
  title: string;
  subtitle?: string;
  headers: string[];
  rows: string[][];
  /** Optional CSS width per column (e.g. `'12%'`) for the printed table. */
  columnWidths?: string[];
};

export const CSV_SEPARATOR = ';';

/** Quotes a cell when it contains the separator, a quote or a line break. */
export const csvCell = (value: string, separator: string = CSV_SEPARATOR): string => {
  const text = value ?? '';
  const needsQuotes =
    text.includes(separator) || text.includes('"') || text.includes('\n') || text.includes('\r');
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Builds the CSV text (with BOM so Excel reads accents correctly). */
export const toCsv = (table: Pick<ExportTable, 'headers' | 'rows'>, separator: string = CSV_SEPARATOR): string => {
  const lines = [table.headers, ...table.rows].map((cells) =>
    cells.map((cell) => csvCell(cell, separator)).join(separator),
  );
  return `﻿${lines.join('\r\n')}\r\n`;
};

/** `alunas-2026-10-19.csv`: a stable, sortable file name. */
export const exportFileName = (prefix: string, extension: string, date: Date = new Date()): string => {
  const safePrefix = prefix
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'export';
  const stamp = date.toISOString().slice(0, 10);
  return `${safePrefix}-${stamp}.${extension}`;
};

/** Triggers a browser download of a text file. */
export const downloadTextFile = (fileName: string, content: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Give the browser a moment to start the download before revoking the URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const escapeHtml = (value: string): string =>
  (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const PRINT_STYLES = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; padding: 16px; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  p.subtitle { font-size: 11px; color: #6b7280; margin: 0 0 12px; }
  table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 9.5px; line-height: 1.3; }
  thead { display: table-header-group; }
  th, td { border: 1px solid #d1d5db; padding: 4px 5px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
  th { background: #f3f4f6; font-weight: 600; }
  tbody tr:nth-child(even) td { background: #fafafa; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  @media screen { body { max-width: 1400px; margin: 0 auto; } }
`;

/** Full HTML document for the printable version. */
export const buildPrintDocument = (table: ExportTable, lang: string = 'pt'): string => {
  const colgroup = table.columnWidths?.length
    ? `<colgroup>${table.columnWidths.map((width) => `<col style="width:${escapeHtml(width)}" />`).join('')}</colgroup>`
    : '';
  const headerCells = table.headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
  const bodyRows = table.rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('\n');
  const subtitle = table.subtitle ? `<p class="subtitle">${escapeHtml(table.subtitle)}</p>` : '';

  return `<!doctype html>
<html lang="${escapeHtml(lang)}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(table.title)}</title>
<style>${PRINT_STYLES}</style>
</head>
<body>
<h1>${escapeHtml(table.title)}</h1>
${subtitle}
<table>
${colgroup}
<thead><tr>${headerCells}</tr></thead>
<tbody>
${bodyRows}
</tbody>
</table>
</body>
</html>`;
};

/**
 * Prints the document from a hidden same-origin iframe, so no pop-up window
 * is involved (pop-up blockers never get a say). The browser's print dialog
 * offers "Save as PDF". The iframe is removed once printing ends.
 */
export const printHtmlDocument = (html: string): void => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'print';
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
    opacity: '0',
  });

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  iframe.addEventListener('load', () => {
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      cleanup();
      return;
    }
    printWindow.addEventListener('afterprint', cleanup);
    // Safety net in case `afterprint` never fires.
    window.setTimeout(cleanup, 5 * 60 * 1000);
    printWindow.focus();
    printWindow.print();
  });

  iframe.srcdoc = html;
  document.body.appendChild(iframe);
};
